import { useRef, useEffect } from 'react';

const BODY_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

interface Props {
  name: string;
  phone: string;
  notes: string;
  onNameChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  onNotesChange: (v: string) => void;
  accentColor: string;
  headingFont: string;
  onContinue: () => void;
}

export function KioskClientForm({ name, phone, notes, onNameChange, onPhoneChange, onNotesChange, accentColor, headingFont, onContinue }: Props) {
  const nameRef = useRef<HTMLInputElement>(null);
  const valid = name.trim().length >= 2 && phone.replace(/\D/g, '').length >= 7;

  useEffect(() => { nameRef.current?.focus(); }, []);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 16px', borderRadius: 12,
    border: '1px solid #E5E5EA', fontSize: 16, fontFamily: BODY_FONT,
    color: '#1A1A1A', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    background: '#fff',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13, fontWeight: 500, color: '#636366', fontFamily: BODY_FONT,
    display: 'block', marginBottom: 6,
  };

  return (
    <div style={{ padding: '0 24px' }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1A1A1A', fontFamily: headingFont, margin: '28px 0 16px' }}>
        Almost there!
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label style={labelStyle}>Your name</label>
          <input ref={nameRef} type="text" value={name} onChange={e => onNameChange(e.target.value)}
            placeholder="Full name" autoCapitalize="words" style={inputStyle}
            onFocus={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}14`; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#E5E5EA'; e.currentTarget.style.boxShadow = 'none'; }} />
        </div>
        <div>
          <label style={labelStyle}>Phone number</label>
          <input type="tel" inputMode="tel" value={formatPhone(phone)}
            onChange={e => onPhoneChange(e.target.value.replace(/\D/g, ''))}
            placeholder="(416) 555-0123" style={inputStyle}
            onFocus={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}14`; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#E5E5EA'; e.currentTarget.style.boxShadow = 'none'; }} />
        </div>
        <div>
          <label style={labelStyle}>Anything we should know? <span style={{ fontWeight: 400, color: '#ADB5BD' }}>(optional)</span></label>
          <textarea value={notes} onChange={e => onNotesChange(e.target.value)}
            placeholder="Special requests, preferences..." rows={2}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
            onFocus={e => { e.currentTarget.style.borderColor = accentColor; e.currentTarget.style.boxShadow = `0 0 0 3px ${accentColor}14`; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#E5E5EA'; e.currentTarget.style.boxShadow = 'none'; }} />
        </div>
      </div>

      <button onClick={onContinue} disabled={!valid} style={{
        width: '100%', marginTop: 24, padding: '15px 0', borderRadius: 14, border: 'none',
        background: valid ? accentColor : '#E5E5EA', color: valid ? '#fff' : '#ADB5BD',
        fontSize: 16, fontWeight: 600, fontFamily: BODY_FONT,
        cursor: valid ? 'pointer' : 'default', transition: 'all 200ms ease',
      }}>
        Review booking
      </button>
    </div>
  );
}
